// ... existing code ...
import React, { useReducer, useCallback, useEffect } from "react";
import {
  Button,
  DatePicker,
  Popover,
  TextField,
  BlockStack,
  InlineStack,
} from "@shopify/polaris";

import { CalendarIcon, XCircleIcon } from "@shopify/polaris-icons";
import { parseDate, dateRegex, isValidDate } from "./utils/dateUtils";
import { validateDateInput } from "./utils/dateValidation";
import {
  datePickerReducer,
  initDatePickerState,
} from "./utils/datePickerReducer";

type DatePickerPopup2Props = {
  label: string;
  date: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  allowClear?: boolean;
  disableDatesAfter?: Date;
  disableDatesBefore?: Date;
  showTodayButton?: boolean;
  requiredIndicator?: boolean;
};

export function DatePickerPopup2({
  label,
  date,
  onChange,
  placeholder = "MM/DD/YYYY",
  error,
  disabled = false,
  allowClear = true,
  disableDatesAfter,
  disableDatesBefore,
  showTodayButton = true,
  requiredIndicator = false,
}: DatePickerPopup2Props) {
  // Use reducer for state management
  const [state, dispatch] = useReducer(
    datePickerReducer,
    date,
    initDatePickerState
  );

  const {
    popoverActive,
    displayMonth,
    displayYear,
    inputValue,
    formatError,
    internalValue,
    isEditing,
  } = state;

  // Debug logging for validation state
  useEffect(() => {
    console.log("DatePickerPopup2 state:", {
      inputValue,
      formatError,
      isEditing,
      externalError: error,
      displayError: formatError || (!isEditing ? error : undefined),
    });
  }, [inputValue, formatError, isEditing, error]);

  // Update state when date prop changes
  useEffect(() => {
    dispatch({ type: "SET_DATE", payload: date });
  }, [date]);

  // Toggle popover
  const togglePopoverActive = useCallback(() => {
    if (!disabled) {
      dispatch({ type: "TOGGLE_POPOVER" });
    }
  }, [disabled]);

  // Handle month change in calendar
  const handleMonthChange = useCallback((month: number, year: number) => {
    dispatch({ type: "SET_MONTH_YEAR", payload: { month, year } });
  }, []);

  // Handle date selection from calendar
  const handleDateSelection = useCallback(
    ({ start }: { start: Date; end: Date }) => {
      dispatch({ type: "SET_POPOVER", payload: false });
      dispatch({ type: "SET_DATE", payload: start });
      onChange(start);
    },
    [onChange]
  );

  // Handle clear date button click
  const handleClearDate = useCallback(() => {
    dispatch({ type: "CLEAR_DATE" });
    onChange(null);
  }, [onChange]);

  // Handle text field change
  const handleTextFieldChange = useCallback(
    (value: string) => {
      console.log("handleTextFieldChange:", value);

      // Check for invalid characters immediately
      const hasInvalidChars = /[^\d\/]/.test(value);
      if (hasInvalidChars) {
        console.log("Invalid characters detected");
        dispatch({
          type: "SET_FORMAT_ERROR",
          payload: "Date can only contain numbers and slashes",
        });
      }

      // Check for invalid year length immediately
      const invalidYearLength = /^(\d{1,2})\/(\d{1,2})\/(\d{5,})$/.test(value);
      if (invalidYearLength) {
        console.log("Invalid year length detected");
        dispatch({
          type: "SET_FORMAT_ERROR",
          payload: "Year must be 2 or 4 digits",
        });
      }

      // Update the input value
      dispatch({ type: "SET_INPUT_VALUE", payload: value });

      // If empty, clear the date
      if (value === "") {
        onChange(null);
        return;
      }

      // Check if this is a complete date format with a 4-digit year
      // We only want to validate and update the parent when we have a complete 4-digit year
      const isCompleteFullYearDate = dateRegex.fullYear.test(value);

      // For 2-digit years, we don't want to validate or update the parent yet
      // as the user might still be typing (e.g., '01/01/20' -> '01/01/2025')
      const isTwoDigitYear = dateRegex.shortYear.test(value);

      if (isCompleteFullYearDate) {
        // For complete dates with 4-digit years, check if it's valid
        const validationError = validateDateInput(value, true);

        if (!validationError) {
          // If valid, parse and update the parent
          const parsedDate = parseDate(value, false, true);
          if (parsedDate) {
            onChange(parsedDate);
          }
        } else {
          // If invalid but complete format, show the error but don't clear the date yet
          console.log("Validation error during typing:", validationError);
          dispatch({
            type: "SET_FORMAT_ERROR",
            payload: validationError,
          });
        }
      } else if (isTwoDigitYear) {
        // For 2-digit years, don't validate or update the parent yet
        // Just clear any format errors that might have been set
        if (!hasInvalidChars && !invalidYearLength) {
          dispatch({ type: "SET_FORMAT_ERROR", payload: undefined });
        }
      } else {
        // For partial inputs, clear any format errors that might have been set
        // (unless we already detected invalid chars or year length)
        if (!hasInvalidChars && !invalidYearLength) {
          dispatch({ type: "SET_FORMAT_ERROR", payload: undefined });
        }
      }
    },
    [onChange]
  );

  // Handle text field blur
  const handleTextFieldBlur = useCallback(() => {
    console.log("handleTextFieldBlur, inputValue:", inputValue);

    // If empty, clear the date and exit
    if (!inputValue) {
      dispatch({ type: "CLEAR_DATE" });
      onChange(null);
      return;
    }

    // Check if this is a complete date with a 4-digit year
    const isCompleteFullYearDate = dateRegex.fullYear.test(inputValue);

    // Check if this is a complete date with a 2-digit year
    const isTwoDigitYear = dateRegex.shortYear.test(inputValue);

    // For partial inputs like "03/16/202" or incomplete dates like "03/" or "03/16"
    const isPartialInput = !isCompleteFullYearDate && !isTwoDigitYear;

    if (isPartialInput) {
      console.log("Partial input detected on blur, showing validation error");

      // For partial inputs on blur, show a validation error
      dispatch({
        type: "SET_FORMAT_ERROR",
        payload: "Please enter a complete date in MM/DD/YYYY format",
      });

      // Exit editing mode to show the error
      dispatch({ type: "SET_EDITING", payload: false });

      // Set the date to null
      onChange(null);
      return;
    }

    // For two-digit years, validate and convert to four-digit years on blur
    if (isTwoDigitYear) {
      console.log(
        "Two-digit year detected on blur, validating and converting to four-digit year"
      );

      // First check if it's a valid calendar date (e.g., not 02/30/2020)
      // Extract the month, day, and year from the input
      const match = inputValue.match(dateRegex.shortYear);
      if (match) {
        const month = parseInt(match[1], 10);
        const day = parseInt(match[2], 10);
        const year = parseInt(match[3], 10);

        // Convert to 4-digit year for validation
        const fullYear = year < 50 ? 2000 + year : 1900 + year;

        // Check if it's a valid calendar date
        if (!isValidDate(month, day, fullYear)) {
          console.log("Invalid calendar date detected:", inputValue);
          dispatch({
            type: "SET_FORMAT_ERROR",
            payload: "Please enter a valid calendar date",
          });

          // Exit editing mode to show the error
          dispatch({ type: "SET_EDITING", payload: false });

          // Set the date to null
          onChange(null);
          return;
        }
      }

      // Validate the date to catch other validation errors
      const validationError = validateDateInput(inputValue, false);
      if (validationError) {
        console.log("Validation error for two-digit year:", validationError);
        dispatch({
          type: "SET_FORMAT_ERROR",
          payload: validationError,
        });

        // Exit editing mode to show the error
        dispatch({ type: "SET_EDITING", payload: false });

        // Set the date to null
        onChange(null);
      } else {
        // If it's valid, convert to four-digit year for display
        const parsedDate = parseDate(inputValue, false, false);
        if (parsedDate) {
          // Update both the input value and internal value
          dispatch({
            type: "SET_DATE",
            payload: parsedDate,
          });

          // Update the parent with the parsed date
          onChange(parsedDate);
        }
      }
      return;
    }

    // Only validate complete dates with 4-digit years
    console.log("Validating complete date with 4-digit year");

    // Check for validation errors
    const validationError = validateDateInput(inputValue, false);
    console.log("Validation result:", validationError || "valid");

    if (validationError) {
      // If there's an error, set the date to null
      onChange(null);

      // Ensure the error is displayed by setting it directly
      dispatch({
        type: "SET_FORMAT_ERROR",
        payload: validationError,
      });

      // Exit editing mode to show the error
      dispatch({ type: "SET_EDITING", payload: false });
    } else {
      // If it's valid, update the parent with the parsed date
      const parsedDate = parseDate(inputValue, false, false);
      if (parsedDate) {
        onChange(parsedDate);
        dispatch({ type: "VALIDATE_INPUT" });
      } else {
        // This should not happen if validateDateInput returned no errors,
        // but just in case, handle it
        dispatch({
          type: "SET_FORMAT_ERROR",
          payload: "Invalid date format",
        });
        onChange(null);
      }
    }
  }, [inputValue, onChange, dispatch]);

  // Handle text field focus
  const handleTextFieldFocus = useCallback(() => {
    console.log("handleTextFieldFocus");
    dispatch({ type: "SET_EDITING", payload: true });
  }, []);

  // Handle today button click
  const handleTodayClick = useCallback(() => {
    const today = new Date();
    dispatch({ type: "SELECT_TODAY" });
    onChange(today);
  }, [onChange]);

  // Determine the error to display
  // If there's a format error, it takes precedence over external errors
  // If we're editing, don't show external errors
  const displayError = formatError || (!isEditing ? error : undefined);

  // Debug log the current error state
  useEffect(() => {
    if (displayError) {
      console.log("Displaying error:", displayError);
    }
  }, [displayError]);

  const activator = (
    <TextField
      label={label}
      value={inputValue}
      onChange={handleTextFieldChange}
      onBlur={handleTextFieldBlur}
      onFocus={handleTextFieldFocus}
      placeholder={placeholder}
      autoComplete="off"
      error={displayError}
      disabled={disabled}
      requiredIndicator={requiredIndicator}
      prefix={
        <Button
          icon={CalendarIcon}
          onClick={togglePopoverActive}
          accessibilityLabel="Open calendar"
          disabled={disabled}
        />
      }
      connectedRight={
        allowClear && (internalValue || inputValue) ? (
          <Button
            icon={XCircleIcon}
            onClick={handleClearDate}
            accessibilityLabel="Clear date"
            disabled={disabled}
          />
        ) : null
      }
    />
  );

  return (
    <Popover
      active={popoverActive}
      activator={activator}
      autofocusTarget="none"
      onClose={togglePopoverActive}
    >
      <Popover.Pane>
        <BlockStack>
          <div style={{ padding: "16px" }}>
            <DatePicker
              month={displayMonth}
              year={displayYear}
              onChange={handleDateSelection}
              onMonthChange={handleMonthChange}
              selected={
                internalValue
                  ? { start: internalValue, end: internalValue }
                  : undefined
              }
              disableDatesBefore={disableDatesBefore}
              disableDatesAfter={disableDatesAfter}
            />
          </div>
          <div style={{ padding: "0 16px 16px" }}>
            <InlineStack gap="200">
              {showTodayButton && (
                <Button
                  onClick={handleTodayClick}
                  fullWidth
                  data-testid="today-button"
                >
                  Today
                </Button>
              )}
              <Button
                onClick={togglePopoverActive}
                variant="tertiary"
                fullWidth
              >
                Cancel
              </Button>
            </InlineStack>
          </div>
        </BlockStack>
      </Popover.Pane>
    </Popover>
  );
}

export default DatePickerPopup2;
